<script lang="ts">
  // The application frame: one header, one content area.
  //
  // The header carries identity on the left and the session on the right. Account state used to sit
  // in a row of its own at the top of the home view, which cost a full band of vertical space on
  // every screen to say things that never change while you work — which Revit you are attached to,
  // who you are signed in as. In a Revit side panel that band is expensive, so it lives in the
  // chrome now, where a status bar belongs.
  //
  // The dialogs live here too, because the buttons that open them do.

  import { onMount } from "svelte";
  import ApibimLogo from "@/components/ApibimLogo.svelte";
  import HomeView from "@/views/HomeView.svelte";
  import PublishDialog from "@/catalog/PublishDialog.svelte";
  import BatchPublishDialog from "@/catalog/BatchPublishDialog.svelte";
  import ModerationDialog from "@/catalog/ModerationDialog.svelte";
  import OrganizationDialog from "@/catalog/OrganizationDialog.svelte";
  import { AppButton, AppSelect } from "@/components/ui";
  import { transport } from "@/transport";
  import { auth } from "@/stores/auth.svelte";
  import { catalog } from "@/catalog/catalogStore.svelte";
  import {
    t,
    locale,
    persistLocale,
    SUPPORTED_LOCALES,
    LOCALE_NAMES,
  } from "@/i18n/index.svelte";
  import type { SupportedLocale } from "@/i18n/index.svelte";

  /** Which host we are attached to, as one string — used both as the label and as its own tooltip. */
  const modeLabel = $derived.by(() => {
    if (!transport.caps.revit) return t("mode.browser");
    return transport.caps.revitYear
      ? t("mode.revit", { year: transport.caps.revitYear })
      : t("mode.revitUnknownYear");
  });

  /** The company site. Opened in the system browser, never inside the panel — see below. */
  const SITE_URL = "https://apibim.com";

  /**
   * Inside Revit, ask the host to launch the browser instead of letting the link do it.
   *
   * The anchor stays a real anchor so the browser build keeps middle-click, "copy link address" and
   * everything else an anchor gives for free. But in the panel, target="_blank" only worked by way
   * of WebView2 raising NewWindowRequested, and that route cannot tell the page whether anything
   * happened — a failure to launch and a click that never registered look identical from here. The
   * host command reports back, and logs when the shell refuses.
   */
  async function openSite(event: MouseEvent) {
    if (!transport.caps.revit) return; // plain browser: let the anchor be an anchor

    event.preventDefault();
    try {
      await transport.invoke("OpenExternalUrl", { url: SITE_URL });
    } catch {
      // Nothing useful to say in the header, and nothing the user can act on: the host has already
      // logged why. The site is reachable by typing its name.
    }
  }

  function onLocaleChange(event: Event) {
    const next = (event.target as HTMLSelectElement).value as SupportedLocale;
    locale.value = next;
    persistLocale(next);
  }

  // Keep the document title localized; the Revit host mirrors this into the WPF window title.
  $effect(() => {
    document.title = `${t("app.title")} - ${t("app.tagline")}`;
  });

  // Publishing is Revit-only (it reads a local .rfa) and requires a session.
  const canPublish = () =>
    transport.caps.revit && auth.state.status === "signed-in";
  let showPublish = $state(false);
  let showBatchPublish = $state(false);

  // Moderation runs in either environment now that the API accepts a browser session; the role is
  // what gates it, and the API enforces that regardless of what this shows.
  const canModerate = () =>
    auth.state.status === "signed-in" && auth.state.isModerator;
  let showModeration = $state(false);

  // The organization entry appears for anyone it can do something for: a member (to read the roster),
  // an admin (to manage it), or the platform owner (who creates organizations in the first place).
  const canOpenOrg = () =>
    auth.state.status === "signed-in" &&
    (auth.state.organizationId !== null || auth.state.isSuperAdmin);
  let showOrganization = $state(false);

  function onPublished() {
    // The new family is live; refresh the grid so it appears, then close.
    void catalog.load();
    showPublish = false;
  }

  function onBatchPublished() {
    // The grid catches up, but the dialog stays open: after a run of forty the per-family result is
    // the point, and closing over it would throw away the only report of what failed.
    void catalog.load();
  }

  function onModerated() {
    // An approval may have made a new family visible; refresh the grid to reflect it.
    void catalog.load();
  }

  function onOrganizationChanged() {
    // Membership decides which shelves exist and what is on them, so both the session and the grid
    // have to catch up — someone who just joined should not have to sign out to see the library.
    void auth.refresh();
    void catalog.load();
  }

  // Sign-in finishes in a separate browser window; re-check the session when the panel regains focus
  // so it flips to signed-in on return without any further action.
  function onFocus() {
    if (auth.state.status !== "signed-in") void auth.refresh();
  }

  onMount(() => {
    void auth.refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  });
</script>

<div class="min-h-full flex flex-col bg-page">
  <header
    class="flex items-center gap-3 px-6 h-16 border-b border-line shrink-0"
  >
    <!-- target="_blank" is what keeps the panel on the app. In the browser it opens a tab; inside
         Revit the WebView2 host intercepts the request and hands the URL to the system browser
         (see DominusWindow), because a panel that navigated to the marketing site would
         have no way back — there is no address bar and no Back button. -->
    <a
      href={SITE_URL}
      target="_blank"
      rel="noopener noreferrer"
      class="shrink-0 rounded-[--brand-radius-control] text-ink hover:opacity-70 transition-opacity focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
      title={SITE_URL}
      onclick={openSite}
    >
      <ApibimLogo height={22} />
    </a>

    <span class="w-px h-6 bg-line shrink-0" aria-hidden="true"></span>

    <span class="flex-1"></span>

    <!-- Which host we are attached to. A dot rather than a word because it is glanced at, not
         read: lit means a live Revit session, muted means the plain browser. -->
    <span
      class="flex items-center gap-2 shrink-0 text-xs text-ink-muted max-w-48"
      title={modeLabel}
    >
      <span
        class="inline-block w-2 h-2 rounded-full shrink-0 {transport.caps.revit
          ? 'bg-accent'
          : 'bg-ink-muted'}"
        aria-hidden="true"
      ></span>
      <!-- The dot survives every width; it is the part that is glanced at rather than read, and
           the label it loses is still on the tooltip. -->
      <span class="hidden md:inline truncate">{modeLabel}</span>
    </span>

    <nav class="flex items-center gap-2 shrink-0">
      {#if auth.state.status === "signed-in"}
        {#if canOpenOrg()}
          <AppButton
            variant="secondary"
            size="xs"
            onclick={() => (showOrganization = true)}
          >
            {t("org.open")}
          </AppButton>
        {/if}
        {#if canModerate()}
          <AppButton
            variant="secondary"
            size="xs"
            onclick={() => (showModeration = true)}
          >
            {t("moderation.open")}
          </AppButton>
        {/if}
        {#if canPublish()}
          <AppButton
            variant="secondary"
            size="xs"
            onclick={() => (showBatchPublish = true)}
          >
            {t("batch.open")}
          </AppButton>
        {/if}
        {#if canPublish()}
          <AppButton
            variant="primary"
            size="xs"
            onclick={() => (showPublish = true)}
          >
            {t("publish.open")}
          </AppButton>
        {/if}
        <span class="hidden md:inline text-xs text-ink-muted truncate max-w-40">
          {auth.state.displayName ?? auth.state.email}
        </span>
        <AppButton variant="ghost" size="xs" onclick={() => auth.signOut()}>
          {t("auth.signOut")}
        </AppButton>
      {:else if auth.state.canSignIn}
        <AppButton
          variant="primary"
          size="xs"
          disabled={auth.state.status === "signing-in"}
          onclick={() => auth.signIn()}
        >
          {auth.state.status === "signing-in"
            ? t("auth.signingIn")
            : t("auth.signIn")}
        </AppButton>
      {/if}

      <label>
        <span class="sr-only">{t("common.language")}</span>
        <AppSelect size="xs" value={locale.value} onchange={onLocaleChange}>
          {#each SUPPORTED_LOCALES as tag (tag)}
            <option value={tag}>{LOCALE_NAMES[tag]}</option>
          {/each}
        </AppSelect>
      </label>
    </nav>
  </header>

  <main class="flex-1 p-6 overflow-auto">
    <HomeView />
  </main>

  {#if showPublish}
    <PublishDialog
      onclose={() => (showPublish = false)}
      onpublished={onPublished}
    />
  {/if}
  {#if showBatchPublish}
    <BatchPublishDialog
      onclose={() => (showBatchPublish = false)}
      onpublished={onBatchPublished}
    />
  {/if}
  {#if showModeration}
    <ModerationDialog
      onclose={() => (showModeration = false)}
      onchanged={onModerated}
    />
  {/if}
  {#if showOrganization}
    <OrganizationDialog
      onclose={() => (showOrganization = false)}
      onchanged={onOrganizationChanged}
    />
  {/if}
</div>
